import { isEmpty, isEqual, omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmountedRef } from 'ahooks';
import { useSocketsContext } from '../SocketsProvider/Sockets.context.ts';
import {
  tWapiAccountConnectionUpdatedEvent,
  tWapiNotificationCreatedEvent,
  tWorkspaceAccountUpdateEvent,
} from '../SocketsProvider/Sockets.types.ts';
import { useUserContext } from '../UserProvider/User.context';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context';
import { initialState, REFETCH_RETRY_DELAY_MS } from './Integrations.constants';
import { eIntegrationStatus, eIntegrationType } from './Integrations.enums';
import { upsertIntegration } from './Integrations.functions';
import { IntegrationsService } from './Integrations.service';
import {
  tFetchOutcome,
  tIntegration,
  tIntegrationsContext,
} from './Integrations.types';
import { useToastContext } from '../ToastProvider/Toast.hook.ts';
import { eAlertType } from '../ToastProvider/Toast.enum.ts';
import { withAuthRetry } from 'src/axios/withAuthRetry';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

export const workspace = {
  id: '6904447a-ba16-4be6-9cdf-269325cd31f1',
  hasPremium: false,
  apiKey: 'perron',
  hasChatDaddySession: true,
  facebookPages: [],
};

// INT-1376: the ONLY outcomes that wrote channel slices. Hydration -- which
// tells the disconnected banners "you may now trust these values" -- must key
// on this, never on "the call did not throw". A superseded or workspace-less
// call writes nothing, so opening the gate on it publishes the hardcoded
// DISCONNECTED defaults as established truth.
const wroteSlices = (outcome: tFetchOutcome): boolean =>
  outcome === 'applied' || outcome === 'partial';

// An empty phone number means no number. Dropping it keeps a Go zero value
// from reaching a `??` fallback, which would render a blank label.
const withoutEmptyPhoneNumber = (integration: tIntegration): tIntegration =>
  integration.phoneNumber === ''
    ? omit(integration, 'phoneNumber')
    : integration;

// INT-1516 — the WBS notification types that carry the WhatsApp API activation
// verdict. WBS creates ACCOUNT_CONNECTED only once a record is operational.
//
// INT-1653 (ajuste 9, 19-oct) — ACCOUNT_PAUSED (Meta took the number out of the
// API) and ACCOUNT_RECONNECTED (it came back) change `offboardedAt` and
// `reconnectionOutcome` on the same row without an account_update, so they
// refetch too: the "reconnecting" and "reconnected" notices read those fields.
const VERDICT_NOTIFICATION_TYPES: readonly string[] = [
  'ACTIVATION_FAILED',
  'ACCOUNT_CONNECTED',
  'ACCOUNT_PAUSED',
  'ACCOUNT_RECONNECTED',
];

const useIntegrations = (): tIntegrationsContext => {
  const { hasSession } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  // Latest-value mirror of currentWorkspaceId, refreshed every render. An
  // in-flight fetchIntegrations call holds its own captured id across an
  // await; comparing that against this ref detects a workspace switch that
  // happened mid-flight (see fetchIntegrations).
  const currentWorkspaceIdRef = useRef(currentWorkspaceId);
  currentWorkspaceIdRef.current = currentWorkspaceId;
  // Coalescing state for connection-event refetches. Leading-edge: the first
  // event fetches immediately (a trailing debounce would delay the connection
  // acknowledgement by the whole window), and events arriving while a fetch is
  // in flight collapse into one trailing call. Connection-state recovery
  // (BUG-807) replays missed events in bursts on reconnect, so without this a
  // reconnect issues one request per replayed event.
  const refetchInFlightRef = useRef(false);
  const refetchQueuedRef = useRef(false);
  // Monotonic token so a superseded response cannot overwrite a newer one.
  const fetchSeqRef = useRef(0);
  // Bumped on unmount and on workspace change. An in-flight run — including one
  // parked in the retry backoff — compares against it at each resumption point
  // and abandons rather than resuming against a workspace the user already left.
  const refetchGenRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // Settles the retry backoff. Cancelling the timer alone would strand the
  // awaited promise, so the run would never reach its finally and the in-flight
  // flag would wedge every later refetch for the life of the provider.
  const retryResolveRef = useRef<(() => void) | undefined>(undefined);
  const unmountedRef = useUnmountedRef();
  const {
    workspaceAccountUpdateEvent,
    catchUpEvent,
    wapiNotificationCreatedEvent,
    wapiAccountConnectionUpdatedEvent,
    tiktokAccountUpdateEvent,
  } = useSocketsContext();
  const { addAlert } = useToastContext();

  const { metaMessengerDisconnectedEvent, metaMessengerConnectedEvent } =
    useSocketsContext();

  metaMessengerConnectedEvent.useSubscription(() => {
    // Meta Messenger and Instagram share a single OAuth token. The disconnect
    // handler already flips both — mirror that here so Instagram chats clear
    // their disconnected banner after the user reconnects from the modal.
    setFacebookIntegration((prev) => {
      return {
        ...prev,
        state: eIntegrationStatus.CONNECTED,
      };
    });
    setInstagramIntegration((prev) => {
      return {
        ...prev,
        state: eIntegrationStatus.CONNECTED,
      };
    });
  });
  metaMessengerDisconnectedEvent.useSubscription(() => {
    setFacebookIntegration((prev) => {
      return {
        ...prev,
        state: eIntegrationStatus.DISCONNECTED,
      };
    });
    setInstagramIntegration((prev) => {
      return {
        ...prev,
        state: eIntegrationStatus.DISCONNECTED,
      };
    });
  });

  const [integrations, setIntegrations] = useState<tIntegration[]>(
    initialState.integrations,
  );

  const [selectedIntegration, setSelectedIntegration] =
    useState<tIntegration | null>(null);

  const [whatsappIntegration, setWhatsappIntegration] = useState<tIntegration>({
    label: eIntegrationType.WHATSAPP_WEB,
    state: eIntegrationStatus.DISCONNECTED,
  });

  const [facebookIntegration, setFacebookIntegration] = useState<tIntegration>({
    label: eIntegrationType.MESSENGER,
    state: eIntegrationStatus.DISCONNECTED,
  });

  const [instagramIntegration, setInstagramIntegration] =
    useState<tIntegration>({
      label: eIntegrationType.INSTAGRAM,
      state: eIntegrationStatus.DISCONNECTED,
    });

  // The WhatsApp API channel is UNKNOWN until the fetch resolves. bff-web
  // answers for a workspace that never connected a WABA with a row at
  // NO_ACCOUNT, so this default is what that answer confirms — "don't know"
  // must never be published as the alarm a real disconnect raises.
  const [whatsappBusinessIntegration, setWhatsappBusinessIntegration] =
    useState<tIntegration>({
      label: eIntegrationType.WHATSAPP_API,
      state: eIntegrationStatus.NO_ACCOUNT,
    });

  const [tiktokIntegration, setTiktokIntegration] = useState<tIntegration>({
    label: eIntegrationType.TIKTOK,
    state: eIntegrationStatus.DISCONNECTED,
  });

  const [isIntegrationModalOpen, setIsIntegrationModalOpen] =
    useState<boolean>(false);

  // Tracks whether the initial getIntegrations fetch has resolved.
  // Consumers (e.g. the disconnected banner) should wait on this before
  // reacting to the default DISCONNECTED state — otherwise the banner flashes
  // for the duration of the round-trip.
  const [isHydrated, setIsHydrated] = useState<boolean>(false);
  const [channelsInLatestResponse, setChannelsInLatestResponse] = useState<
    eIntegrationType[]
  >(initialState.channelsInLatestResponse);

  // Fetches the full integration set and updates every channel slice plus the
  // `integrations` array. Extracted (INT-938) so both the workspace-mount
  // effect and the socket catch-up subscription can invoke it.
  const fetchIntegrations = useCallback(async (): Promise<tFetchOutcome> => {
    // Capture the workspace id before the first await so the request targets
    // the workspace that was active when the fetch started (CLAUDE.md "Async
    // Guards and WebSocket Race Conditions").
    const workspaceId = currentWorkspaceId;

    if (isEmpty(workspaceId)) {
      return 'stale';
    }

    // The workspace guard below catches a workspace switch, but not a
    // superseded call for the same workspace. A connection event and the
    // catch-up subscription can both have a fetch in flight, and the
    // integrations endpoint fans out to several services, so out-of-order
    // resolution is ordinary rather than exotic.
    const seq = ++fetchSeqRef.current;

    try {
      // BUG-868: the boot fan-out can 401 when Firebase hasn't settled. Without
      // this retry the catch below returned 'failed', no slice was written, and
      // the .finally() hydrated anyway — Messenger stuck on its DISCONNECTED
      // default (false banner) until a full reload.
      const response = await withAuthRetry(
        () =>
          IntegrationsService.getIntegrations({
            uuid: workspaceId as string,
          }),
        {
          shouldAbort: () =>
            workspaceId !== currentWorkspaceIdRef.current ||
            seq !== fetchSeqRef.current ||
            !!unmountedRef.current,
        },
      );

      // A workspace switch during the await makes this response stale.
      // currentWorkspaceIdRef always holds the live workspace id; if it no
      // longer matches the captured id, drop the response so it cannot
      // overwrite the now-active workspace's integration state.
      if (workspaceId !== currentWorkspaceIdRef.current) {
        return 'stale';
      }

      if (seq !== fetchSeqRef.current) {
        return 'stale';
      }

      if (!response.success) {
        return 'failed';
      }

      // The endpoint answers HTTP 200 with success=true even when one channel's
      // fan-out job failed: that channel's row is absent and its error lands in
      // `errors`. Absence means failure and nothing else — a workspace with no
      // WABA still gets a WHATSAPP_API row, because bff-web turns the 404 into
      // NO_ACCOUNT (or DISCONNECTED when the 404 carries a retirement
      // timestamp) instead of propagating it. A non-empty errors list is
      // therefore not "no information": the channels that answered are real,
      // and discarding the payload would leave the workspace with an empty
      // array and every slice disconnected.
      //
      // Apply the rows that arrived, and merge rather than replace so a channel
      // the response omits keeps its previous row instead of vanishing — which
      // is what would otherwise make slice-gated and array-gated UI disagree.
      const isPartial = Boolean(response.errors?.length);
      const rows = response.data.integrations.map(withoutEmptyPhoneNumber);

      {
        for (const integration of rows) {
          if (integration.label === eIntegrationType.WHATSAPP_WEB) {
            setWhatsappIntegration((prev) =>
              isEqual(prev, integration) ? prev : integration,
            );
          }
          if (integration.label === eIntegrationType.MESSENGER) {
            setFacebookIntegration((prev) =>
              isEqual(prev, integration) ? prev : integration,
            );
          }
          if (integration.label === eIntegrationType.INSTAGRAM) {
            setInstagramIntegration((prev) =>
              isEqual(prev, integration) ? prev : integration,
            );
          }
          if (integration.label === eIntegrationType.WHATSAPP_API) {
            setWhatsappBusinessIntegration((prev) =>
              isEqual(prev, integration) ? prev : integration,
            );
          }
          if (integration.label === eIntegrationType.TIKTOK) {
            setTiktokIntegration((prev) =>
              isEqual(prev, integration) ? prev : integration,
            );
          }
        }

        setIntegrations((prev) => {
          const next = isPartial
            ? rows.reduce((acc, row) => upsertIntegration(acc, row), prev)
            : rows;

          return isEqual(prev, next) ? prev : next;
        });

        // Recorded from the response rather than from the merged array, which
        // keeps rows this round never mentioned.
        setChannelsInLatestResponse((prev) => {
          const next = response.data.integrations.map(
            (integration) => integration.label,
          );

          return isEqual(prev, next) ? prev : next;
        });

        // Still not retried: a degraded downstream is not a transient blip, and
        // the channels that answered are already applied.
        return isPartial ? 'partial' : 'applied';
      }
    } catch (e) {
      LLog.error(
        {
          component: LOG_COMPONENTS.INTEGRATIONS,
          method: 'Integrations.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en IntegrationsProvider',
        e as unknown as Error,
      );

      // A rejection that a newer call already superseded is not worth retrying.
      if (
        workspaceId !== currentWorkspaceIdRef.current ||
        seq !== fetchSeqRef.current
      ) {
        return 'stale';
      }

      return 'failed';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [currentWorkspaceId]);

  // Connection events are the only path that now carries a live connection
  // change, and fetchIntegrations swallows its failures into a log without
  // touching state. Retry once so a transient failure does not leave a
  // disconnect unrepresented until the next catch-up.
  const runConnectionRefetch = useCallback(async (): Promise<void> => {
    if (refetchInFlightRef.current) {
      refetchQueuedRef.current = true;
      return;
    }

    const generation = refetchGenRef.current;
    refetchInFlightRef.current = true;

    // INT-1376: if the mount fetch failed, the provider is still unhydrated and
    // every disconnected banner is suppressed. Recovering here is what stops
    // the fix from trading a false "disconnected" for a banner that never warns
    // again for the rest of the session. Monotonic — only false -> true — so a
    // background re-sync still cannot flicker the banner, and gated on this
    // run still owning the workspace generation so a superseded run cannot
    // hydrate the workspace the user just switched to.
    const hydrateIfStillLive = () => {
      if (generation === refetchGenRef.current && !unmountedRef.current) {
        setIsHydrated(true);
      }
    };

    try {
      const outcome = await fetchIntegrations();

      if (outcome !== 'failed') {
        if (wroteSlices(outcome)) {
          hydrateIfStillLive();
        }
        return;
      }
      if (generation !== refetchGenRef.current || unmountedRef.current) return;

      await new Promise<void>((resolve) => {
        retryResolveRef.current = resolve;
        retryTimerRef.current = setTimeout(() => {
          retryResolveRef.current = undefined;
          resolve();
        }, REFETCH_RETRY_DELAY_MS);
      });

      if (generation !== refetchGenRef.current || unmountedRef.current) return;

      const retryOutcome = await fetchIntegrations();

      if (wroteSlices(retryOutcome)) {
        hydrateIfStillLive();
      }
    } finally {
      // Only the invocation that set the flag clears it, so a workspace change
      // cannot hand a still-running fetch's flag to a second concurrent run.
      refetchInFlightRef.current = false;

      // Always clear the flag, then re-dispatch against live state rather than
      // this run's captured generation: an event that arrived after a workspace
      // switch is still a real event, and the new run re-reads the workspace.
      const queued = refetchQueuedRef.current;
      refetchQueuedRef.current = false;

      if (queued && !unmountedRef.current) {
        // Through the ref, not this closure: a workspace change rebuilds
        // fetchIntegrations, and recursing into the captured binding would
        // re-fetch the workspace the user already left while still dropping
        // the event that queued this drain.
        void runConnectionRefetchRef.current();
      }
    }
  }, [fetchIntegrations, unmountedRef]);

  // Latest-value mirror so an in-flight run drains into the current render's
  // callback rather than the one it captured.
  const runConnectionRefetchRef = useRef(runConnectionRefetch);
  runConnectionRefetchRef.current = runConnectionRefetch;

  // A connection event means backend state changed for some channel. The
  // relay strips `integration_type` from the payload, so the event cannot say
  // which one — refetching is channel-agnostic and, unlike a targeted slice
  // write, updates both the per-channel slices and the `integrations` array
  // that the chat and pipe surfaces gate on.
  workspaceAccountUpdateEvent.useSubscription(
    (value: tWorkspaceAccountUpdateEvent) => {
      // Fail open when workspace_id is absent. The relay injects it from the
      // URL, so it is normally present; this deliberately diverges from the
      // two WhatsApp Web hooks, whose equality check fails closed and would
      // drop an event from any emitter that bypasses the relay.
      if (
        value.workspace_id &&
        value.workspace_id !== currentWorkspaceIdRef.current
      ) {
        return;
      }

      void runConnectionRefetch();
    },
  );

  tiktokAccountUpdateEvent.useSubscription((value) => {
    if (!value?.workspace_id || value.workspace_id !== currentWorkspaceIdRef.current) return;
    // Retire an older in-flight response, then read the committed account state.
    // A delayed revoked event must not undo a newer successful reconnection.
    fetchSeqRef.current += 1;
    void runConnectionRefetch();
  });

  // INT-1516 — a held WhatsApp API record announces nothing, so no
  // account_update follows its verdict. WBS pushes a notification instead:
  // ACTIVATION_FAILED when the activation window closes, ACCOUNT_CONNECTED
  // when the record becomes operational. Either one means the WhatsApp API row
  // changed, so ask the backend, through the same coalesced path; every other
  // notification type says nothing about the connection.
  wapiNotificationCreatedEvent.useSubscription(
    (value: tWapiNotificationCreatedEvent) => {
      if (!VERDICT_NOTIFICATION_TYPES.includes(value?.notification_type)) {
        return;
      }
      // Same fail-open rule as the account_update handler above: only a
      // workspace id that is present and different drops the event.
      if (
        value.workspace_id &&
        value.workspace_id !== currentWorkspaceIdRef.current
      ) {
        return;
      }

      void runConnectionRefetch();
    },
  );

  // INT-1653 / INT-1676 — `whatsapp_business.account.connection_updated` is
  // WBS saying the WhatsApp API row's connection data changed without an
  // account_update: Meta took the number out (`offboardedAt`), gave it back,
  // or a reconnection outcome was written. It carries no state, so ask the
  // backend through the same coalesced path. Same fail-open workspace rule.
  wapiAccountConnectionUpdatedEvent.useSubscription(
    (value: tWapiAccountConnectionUpdatedEvent) => {
      if (
        value?.workspace_id &&
        value.workspace_id !== currentWorkspaceIdRef.current
      ) {
        return;
      }

      void runConnectionRefetch();
    },
  );

  // On unmount and on workspace change, retire any in-flight run: bump the
  // generation so it abandons at its next resumption point, drop a queued
  // trailing call, and clear a pending retry timer. refetchInFlightRef is
  // deliberately not cleared here — doing so would let a second run start while
  // the first is still outstanding, and the first's finally would then clear a
  // flag it no longer owns.
  useEffect(() => {
    return () => {
      refetchGenRef.current += 1;
      refetchQueuedRef.current = false;

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = undefined;
      }

      // Wake the parked run so it reaches its finally and releases the
      // in-flight flag; the generation check after the await makes it abandon.
      retryResolveRef.current?.();
      retryResolveRef.current = undefined;
    };
  }, [currentWorkspaceId]);

  // Initial load + workspace switch: toggle the hydration flag so consumers
  // (e.g. the disconnected banner) can wait for first paint.
  //
  // INT-1376: hydrate ONLY when the call wrote slices. This used to be
  // `.finally(() => setIsHydrated(true))`, which opened the gate even when the
  // fetch threw — publishing the hardcoded DISCONNECTED defaults as
  // established truth. That is the false "todo desconectado" banner: when the
  // BUG-868 retry exhausts its budget the channels are fine and the UI says
  // they are dead. No data must read as "don't know", not as "disconnected".
  //
  // A 'stale' outcome is NOT a reason to hydrate and NOT a reason to retry:
  // either there is no workspace yet, or a newer call already owns hydration.
  useEffect(() => {
    // A workspace switch supersedes this run. Without the guard a stale
    // resolution could hydrate the workspace the user just moved to, using
    // data that was never applied to it.
    let superseded = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let wakeRetry: (() => void) | undefined;

    setIsHydrated(false);

    // Both containers — the aggregate array and the per-channel slices — go
    // back to their defaults alongside the hydration flag, and the selection
    // with them. A partial response for the new workspace writes only the
    // channels it carries and merges the rest over whatever is already there,
    // so a row left standing here is the previous workspace's answer served as
    // this one's.
    setIntegrations(initialState.integrations);
    setChannelsInLatestResponse(initialState.channelsInLatestResponse);
    setSelectedIntegration(initialState.selectedIntegration);
    setWhatsappIntegration(initialState.whatsappIntegration);
    setFacebookIntegration(initialState.facebookIntegration);
    setInstagramIntegration(initialState.instagramIntegration);
    setWhatsappBusinessIntegration(initialState.whatsappBusinessIntegration);
    setTiktokIntegration(initialState.tiktokIntegration);

    const loadAndHydrate = async (): Promise<void> => {
      try {
        const outcome = await fetchIntegrations();

        if (superseded) {
          return;
        }

        if (wroteSlices(outcome)) {
          setIsHydrated(true);
          return;
        }

        // Nothing was written. 'stale' means a newer call (or a later mount for
        // a workspace that did not exist yet) will hydrate instead, so stop.
        if (outcome !== 'failed') {
          return;
        }

        // Only 'failed' is left, and failing closed here would be its own bug:
        // the banners stay suppressed for the whole session, so a channel that
        // was ALREADY down when the tab opened never warns. The socket paths
        // cannot cover it — both catch-up emitters require a prior disconnect
        // (`disconnectedAtRef`), and an account_update only fires on a state
        // CHANGE. So the mount owns one bounded retry, the same budget
        // runConnectionRefetch already uses for connection events.
        await new Promise<void>((resolve) => {
          wakeRetry = resolve;
          retryTimer = setTimeout(() => {
            wakeRetry = undefined;
            resolve();
          }, REFETCH_RETRY_DELAY_MS);
        });

        if (superseded) {
          return;
        }

        const retryOutcome = await fetchIntegrations();

        if (!superseded && wroteSlices(retryOutcome)) {
          setIsHydrated(true);
        }
      } catch (e) {
        // fetchIntegrations resolves its own failures into 'failed'; reaching
        // here means an unexpected throw, which is still "no data" — but it
        // would otherwise be invisible.
        LLog.error(
          {
            component: LOG_COMPONENTS.INTEGRATIONS,
            method: 'Integrations.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Unexpected fetchIntegrations rejection on mount',
          e as unknown as Error,
        );
      }
    };

    void loadAndHydrate();

    return () => {
      superseded = true;

      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }

      // Wake a run parked in the retry backoff so it reaches its next
      // `superseded` check and exits, instead of leaking a pending promise.
      wakeRetry?.();
      wakeRetry = undefined;
    };
  }, [fetchIntegrations]);

  // INT-938: when the socket layer signals a catch-up (reconnect after a drop,
  // or tab-resume after a long hidden interval), re-sync the integration state
  // so a connection change missed while the browser was asleep/offline
  // self-corrects without a manual reload. No isHydrated toggle here — a
  // background re-sync must not flash the chat "no connection" banner.
  catchUpEvent.useSubscription(() => {
    // Shares the coalescer with connection events: a socket reconnect fires
    // this *and* replays the missed connection events, so routing both through
    // one gate keeps a reconnect to a single fan-out instead of concurrent ones.
    void runConnectionRefetch();
  });

  const checkAndDeleteBannedAccount = useCallback(async () => {
    if (!currentWorkspaceId) return;
    try {
      await IntegrationsService.deleteAccount(currentWorkspaceId);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.INTEGRATIONS,
          method: 'Integrations.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en IntegrationsProvider',
        error as unknown as Error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message || 'Ocurrió un error al borrar la cuenta',
      });
    }
  }, [currentWorkspaceId, addAlert]);

  const hasWaBusinessIntegration = useCallback(() => {
    return integrations.some(
      (integration) =>
        integration.label === eIntegrationType.WHATSAPP_API &&
        integration.state === eIntegrationStatus.CONNECTED,
    );
  }, [integrations]);

  // Presence check (entry exists AND state !== NO_ACCOUNT). Distinct from
  // hasWaBusinessIntegration() which requires CONNECTED — the WA Web
  // disconnected banner needs to fire on DISCONNECTED/CONNECTING too, so
  // "is connected" is the wrong gate here.
  const hasWaWebAccount = useCallback(() => {
    const entry = integrations.find(
      (integration) => integration.label === eIntegrationType.WHATSAPP_WEB,
    );
    if (!entry) return false;
    return entry.state !== eIntegrationStatus.NO_ACCOUNT;
  }, [integrations]);

  const hasTiktokIntegration = useCallback(() => {
    return tiktokIntegration.state === eIntegrationStatus.CONNECTED;
  }, [tiktokIntegration.state]);

  return useMemo(
    () => {
      if (!hasSession) return initialState;
      return {
        integrations,
        setIntegrations,
        selectedIntegration,
        setSelectedIntegration,
        whatsappIntegration,
        setWhatsappIntegration,
        facebookIntegration,
        setFacebookIntegration,
        instagramIntegration,
        setInstagramIntegration,
        whatsappBusinessIntegration,
        setWhatsappBusinessIntegration,
        isIntegrationModalOpen,
        setIsIntegrationModalOpen,
        tiktokIntegration,
        setTiktokIntegration,
        hasTiktokIntegration,
        checkAndDeleteBannedAccount,
        hasWaBusinessIntegration,
        hasWaWebAccount,
        // INT-1516 — the same coalesced, retried path connection events use, so a
        // consumer's refetch joins a socket-driven one instead of racing it.
        refetchIntegrations: runConnectionRefetch,
        isHydrated,
        channelsInLatestResponse,
      };
    },
    [
      integrations,
      setIntegrations,
      selectedIntegration,
      setSelectedIntegration,
      whatsappIntegration,
      setWhatsappIntegration,
      facebookIntegration,
      setFacebookIntegration,
      instagramIntegration,
      setInstagramIntegration,
      whatsappBusinessIntegration,
      setWhatsappBusinessIntegration,
      isIntegrationModalOpen,
      setIsIntegrationModalOpen,
      tiktokIntegration,
      setTiktokIntegration,
      hasTiktokIntegration,
      checkAndDeleteBannedAccount,
      hasWaBusinessIntegration,
      hasWaWebAccount,
      runConnectionRefetch,
      isHydrated,
      channelsInLatestResponse,
      hasSession,
    ],
  );
};

export default useIntegrations;
